<template>
  <article
    :class="[
      'grid-item',
      {
        'grid-item--inherit-direction': theme === 'text-only',
        'grid-item--full': theme === 'full',
      },
    ]"
  >
    <div class="grid-item__default__header" v-if="isDefaultOrFull">
      <router-link :to="link">
        <h2>{{ title }}</h2>
        <div class="grid-item__default__header__tags">
          <ul>
            <li>{{ client }}</li>
            <span class="grid-item__default__header__tags--separator"> - </span>
            <li>{{ year }}</li>
          </ul>
        </div>
      </router-link>
    </div>

    <div class="grid-item__text-only" v-if="isTextOnly">
      <div class="grid-item__text-only__content">
        <h2>{{ title }}</h2>
        <p v-if="description">{{ description }}</p>
        <router-link :to="link">
          <button type="click" class="grid-item__text-only__content__btn">
            {{ $t("generals.read") }}
          </button>
        </router-link>
      </div>
    </div>

    <router-link :to="link">
      <div class="grid-item__image" :style="setBg(image)">
        <div class="grid-item__overlay"></div>
      </div>
    </router-link>
  </article>
</template>

<script>
export default {
  name: "grid-item",
  props: {
    title: {
      type: String,
      default: "",
      required: true,
    },
    description: {
      type: String,
      default: "",
    },
    image: {
      type: String,
      default: "",
    },
    client: {
      type: String,
      default: "",
    },
    year: {
      type: [String, Number],
      default: "",
    },
    country: {
      type: String,
      default: "",
    },
    theme: {
      type: String,
      default: "default",
    },
    link: {
      type: String,
      required: true,
    },
  },

  computed: {
    isDefaultOrFull() {
      return this.theme === "default" || this.theme === "full";
    },

    isTextOnly() {
      return this.theme === "text-only";
    },
  },

  methods: {
    setBg(imageUrl) {
      if (this.theme === "text-only") {
        return "";
      }

      return { backgroundImage: `url(${imageUrl})` };
    },
  },
};
</script>

<style lang="scss">
.grid-item {
  height: 30vw;
  display: flex;
  flex-flow: column;
  position: relative;
  width: 100%;
  transition: all 0.2s ease-out;
  max-height: 575px;

  a {
    color: black;
    height: 100%;
  }

  &--inherit-direction {
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    flex-flow: row;

    .grid-item__image {
      display: none;
    }
  }

  &--full {
    height: 50vw;
    max-height: 950px;
  }

  &__image {
    background-size: cover !important;
    margin: 10px 10px 0 0;
    height: 100%;
    width: 100%;
    position: relative;
    background-position: center center !important;
  }

  &__overlay {
    position: absolute;
    height: 100%;
    width: 100%;
    background: rgba(0, 0, 0, 0.5);
    opacity: 0;
    pointer-events: none;
    transition: all 0.2s ease-in;
    top: 0;
  }

  &:hover {
    .grid-item__overlay {
      opacity: 1;
      transition: all 0.2s ease-in;
    }
  }

  &__default {
    &__header {
      position: relative;
      z-index: 4;
      color: black;
      margin-bottom: 15px;

      h2 {
        font-weight: 300;
        font-size: 28px;
        margin-bottom: 5px;
        text-transform: uppercase;
      }

      ul {
        padding: 0;
        margin: 0;
        display: flex;

        li {
          list-style: none;
          font-size: 12px;
          text-transform: uppercase;
        }
      }

      &__tags {
        margin-left: 1;
        font-family: $tertiary;

        &--separator {
          display: block;
          position: relative;
          margin: 0 7px;
          font-size: 13px;
        }
      }
    }
  }

  &__text-only {
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    letter-spacing: 1px;
    margin-top: 40px;
    padding: 20px;
    transition: color 0.3s ease-in-out;

    a {
      color: black;

      &:hover {
        color: rgba(0, 0, 0, 0.45);
        transition: color 0.3s ease-in-out;

        button {
          color: rgba(0, 0, 0, 0.5);
          transition: color 0.3s ease-in-out;
        }
      }
    }

    h2 {
      font-family: $hwcigars;
      font-weight: 300;
      font-size: 26px;
      position: relative;

      &:after {
        position: absolute;
        content: "";
        width: 30px;
        height: 1.5px;
        background: black;
        bottom: -10px;
        left: 0;
        right: 0;
        margin: 0 auto;
      }
    }

    p {
      font-family: $hwcigars;
      font-size: 22px;
      font-weight: 300;
      margin: 30px 0 0;
      display: block;
      position: relative;
    }

    button {
      background: transparent;
      border: 0px;
      font-size: 22px;
      margin-top: 30px;
      font-family: $hwcigars;
      font-weight: 300;
      border-bottom: 1px solid black;
      cursor: pointer;
    }
  }
}

@media (max-width: 1780px) {
  .grid-item {
    height: 36vw;

    &--full {
      height: 58vw;
    }
  }
}

@media (max-width: 1250px) {
  .grid-item {
    &__default {
      &__header {
        h2 {
          font-size: 24px;
        }
      }
    }

    &__text-only {
      h2 {
        font-size: 24px;
      }

      p {
        font-size: 21px;
        line-height: 27px;
      }

      button {
        font-size: 21px;
      }
    }
  }
}

@media (max-width: 1100px) {
  .grid-item {
    &__text-only {
      p {
        font-size: 18px;
        line-height: 20px;
      }

      button {
        font-size: 20px;
      }
    }
  }
}

@media (max-width: 1000px) {
  .grid-item {
    height: 80vw;
    max-height: 520px;
    margin: 0 0 100px;
    justify-content: center;
    align-items: auto;

    &__text-only {
      margin-top: 0;
    }

    &--full {
      margin-top: 0 !important;
    }

    &__default {
      &__header {
        h2 {
          font-size: 18px;
        }
      }
    }

    &__text-only {
      h2,
      button {
        font-size: 18px;
        padding: 0 5px;
      }

      p {
        font-size: 16px;
      }
    }
  }
}
</style>
